/**
 * Dates and sums for the "assign / extend" dialog. Days are `YYYY-MM-DD`
 * strings in the platform calendar (Tashkent) — the backend takes the last
 * day of access, not an instant.
 */

/** Tashkent is UTC+5 all year (no daylight saving), so a fixed offset is exact. */
const TASHKENT_OFFSET_MS = 5 * 3_600_000;
const DAY_MS = 86_400_000;

function parseDay(day: string): { y: number; m: number; d: number } {
  const [y, m, d] = day.split('-').map(Number);
  return { y: y!, m: m!, d: d! };
}

function formatDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Today in Tashkent. */
export function todayInTashkent(now: number = Date.now()): string {
  return formatDay(now + TASHKENT_OFFSET_MS);
}

/** `day` + `months` calendar months; the 31st of January + 1 is the last day of February. */
export function addMonths(day: string, months: number): string {
  const { y, m, d } = parseDay(day);
  const monthIndex = m - 1 + months;
  const lastDay = new Date(Date.UTC(y, monthIndex + 1, 0)).getUTCDate();
  return formatDay(Date.UTC(y, monthIndex, Math.min(d, lastDay)));
}

/** `day` + `days`. */
export function addDays(day: string, days: number): string {
  const { y, m, d } = parseDay(day);
  return formatDay(Date.UTC(y, m - 1, d) + days * DAY_MS);
}

/**
 * The last day of access a period end means: the backend stores the instant
 * the next day begins in Tashkent.
 */
export function lastPaidDay(currentPeriodEnd: string): string {
  return formatDay(Date.parse(currentPeriodEnd) + TASHKENT_OFFSET_MS - DAY_MS);
}

/** What +N months counts from: the end of a period still running, otherwise today. */
export function extensionBase(today: string, currentPeriodEnd: string | null): string {
  if (!currentPeriodEnd) return today;
  const last = lastPaidDay(currentPeriodEnd);
  return last > today ? last : today;
}

/**
 * {@link extensionBase} for a subscription: one moved to Free (cancelled) is
 * not running, so its old end does not count — the extension starts today.
 */
export function extensionBaseFor(
  today: string,
  subscription: { status: string; currentPeriodEnd: string | null } | null,
): string {
  const running = subscription && subscription.status !== 'cancelled' ? subscription.currentPeriodEnd : null;
  return extensionBase(today, running);
}

/** The price of `months` months: a year at the yearly price, otherwise months × the monthly one. */
export function defaultAmount(plan: { priceMonthly: number; priceYearly: number }, months: number): number {
  return months === 12 ? plan.priceYearly : Math.round(plan.priceMonthly * months * 100) / 100;
}

/** "1 500 000", "29,90" → a number; null for anything that is not a positive sum with ≤ 2 decimals. */
export function parseAmount(input: string): number | null {
  const normalized = input.replace(/[\s\u00a0\u202f]/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const value = Number(normalized);
  return value > 0 ? value : null;
}
